/**
 * Product Image Magnifier
 * Creates a 420px round magnifying glass that follows the cursor
 * 100% quality image - works on mobile too
 */

class ProductImageZoom {
  constructor() {
    this.zoomLevel = 2; // Magnification level
    this.lensSize = 420; // Size in pixels
    this.lens = null;
    this.activeImage = null;
    this.highResImageCache = new Map();
    this.isTouchDevice = 'ontouchstart' in window;

    this.boundMouseMove = this.handleMouseMove.bind(this);
    this.boundMouseEnter = this.handleMouseEnter.bind(this);
    this.boundMouseLeave = this.handleMouseLeave.bind(this);
    this.boundTouchStart = this.handleTouchStart.bind(this);
    this.boundTouchMove = this.handleTouchMove.bind(this);
    this.boundTouchEnd = this.handleTouchEnd.bind(this);

    this.init();
  }

  init() {
    // Create the magnifier lens element
    this.createLens();

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.attachToImages());
    } else {
      this.attachToImages();
    }

    // Re-attach when variant changes (image might change)
    document.addEventListener('variant:changed', () => {
      setTimeout(() => this.attachToImages(), 100);
    });

    // Handle dynamic content loading
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          this.attachToImages();
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  createLens() {
    // Remove existing lens if any
    const existingLens = document.querySelector('.product-zoom-lens');
    if (existingLens) {
      existingLens.remove();
    }

    // Create new lens
    this.lens = document.createElement('div');
    this.lens.className = 'product-zoom-lens';
    this.lens.style.cssText = `
      position: fixed;
      width: ${this.lensSize}px;
      height: ${this.lensSize}px;
      border-radius: 50%;
      border: 3px solid #042cdf;
      box-shadow: 0 8px 32px rgba(4, 44, 223, 0.3), 0 0 0 1px rgba(255,255,255,0.8) inset;
      pointer-events: none;
      z-index: 9999;
      display: none;
      background-repeat: no-repeat;
      background-color: #fff;
      cursor: none;
      transition: opacity 0.15s ease;
      opacity: 0;
      image-rendering: high-quality;
      image-rendering: -webkit-optimize-contrast;
    `;

    document.body.appendChild(this.lens);
  }

  attachToImages() {
    // Target main product images on product pages
    const productImages = document.querySelectorAll(
      '.product__media-item img, ' +
      '.product__media img, ' +
      '.product-media-container img, ' +
      '.media-gallery__item img, ' +
      '[data-product-media-type="image"] img, ' +
      '.product__main-photos img, ' +
      '.product-single__photo img'
    );

    productImages.forEach((img) => {
      // Skip if already initialized
      if (img.dataset.zoomInitialized) return;
      img.dataset.zoomInitialized = 'true';

      // Preload high-res image
      this.preloadHighResImage(img);

      // Get the parent container
      const container = img.closest('.product__media-item') ||
                       img.closest('.product__media') ||
                       img.closest('.media-gallery__item') ||
                       img.closest('[data-product-media-type="image"]') ||
                       img.parentElement;

      if (container) {
        container.style.cursor = 'crosshair';

        // Desktop events
        container.addEventListener('mouseenter', this.boundMouseEnter);
        container.addEventListener('mouseleave', this.boundMouseLeave);
        container.addEventListener('mousemove', this.boundMouseMove);

        // Mobile/Touch events
        container.addEventListener('touchstart', this.boundTouchStart, { passive: false });
        container.addEventListener('touchmove', this.boundTouchMove, { passive: false });
        container.addEventListener('touchend', this.boundTouchEnd);
      }
    });
  }

  preloadHighResImage(img) {
    const highResSrc = this.getHighResImage(img);
    if (!this.highResImageCache.has(highResSrc)) {
      const preloadImg = new Image();
      preloadImg.src = highResSrc;
      this.highResImageCache.set(highResSrc, preloadImg);
    }
  }

  handleMouseEnter(e) {
    const img = e.currentTarget.querySelector('img');
    if (!img) return;

    this.activeImage = img;

    // Get the high-res image source (100% quality)
    const highResSrc = this.getHighResImage(img);

    // Set background image for the lens
    this.lens.style.backgroundImage = `url('${highResSrc}')`;
    this.lens.style.display = 'block';

    // Fade in
    requestAnimationFrame(() => {
      this.lens.style.opacity = '1';
    });
  }

  handleMouseLeave() {
    this.lens.style.opacity = '0';
    setTimeout(() => {
      this.lens.style.display = 'none';
    }, 150);
    this.activeImage = null;
  }

  handleMouseMove(e) {
    if (!this.activeImage || !this.lens) return;
    this.updateLensPosition(e.clientX, e.clientY);
  }

  // Touch handlers for mobile
  handleTouchStart(e) {
    const container = e.currentTarget;
    const img = container.querySelector('img');
    if (!img) return;

    // Prevent default to avoid scrolling while zooming
    e.preventDefault();

    this.activeImage = img;

    // Get the high-res image source (100% quality)
    const highResSrc = this.getHighResImage(img);

    // Set background image for the lens
    this.lens.style.backgroundImage = `url('${highResSrc}')`;
    this.lens.style.display = 'block';

    // Fade in
    requestAnimationFrame(() => {
      this.lens.style.opacity = '1';
    });

    // Update position
    const touch = e.touches[0];
    this.updateLensPosition(touch.clientX, touch.clientY);
  }

  handleTouchMove(e) {
    if (!this.activeImage || !this.lens) return;
    e.preventDefault();

    const touch = e.touches[0];
    this.updateLensPosition(touch.clientX, touch.clientY);
  }

  handleTouchEnd() {
    this.lens.style.opacity = '0';
    setTimeout(() => {
      this.lens.style.display = 'none';
    }, 150);
    this.activeImage = null;
  }

  updateLensPosition(clientX, clientY) {
    const img = this.activeImage;
    const rect = img.getBoundingClientRect();

    // Mouse/touch position relative to image
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    // Check if position is within image bounds
    if (x < 0 || x > rect.width || y < 0 || y > rect.height) {
      return;
    }

    // Position lens centered on cursor/touch
    const lensX = clientX - (this.lensSize / 2);
    const lensY = clientY - (this.lensSize / 2);

    this.lens.style.left = `${lensX}px`;
    this.lens.style.top = `${lensY}px`;

    // Get the cached high-res image dimensions
    const highResSrc = this.getHighResImage(img);
    const cachedImg = this.highResImageCache.get(highResSrc);

    // Use natural dimensions from cached image if available, otherwise estimate
    let naturalWidth, naturalHeight;
    if (cachedImg && cachedImg.naturalWidth) {
      naturalWidth = cachedImg.naturalWidth;
      naturalHeight = cachedImg.naturalHeight;
    } else {
      naturalWidth = img.naturalWidth || img.width * this.zoomLevel;
      naturalHeight = img.naturalHeight || img.height * this.zoomLevel;
    }

    // Calculate the zoom ratio
    const zoomWidth = naturalWidth * this.zoomLevel;
    const zoomHeight = naturalHeight * this.zoomLevel;

    // Calculate percentage position
    const percentX = (x / rect.width) * 100;
    const percentY = (y / rect.height) * 100;

    // Set background size and position
    this.lens.style.backgroundSize = `${zoomWidth}px ${zoomHeight}px`;
    this.lens.style.backgroundPosition = `${percentX}% ${percentY}%`;
  }

  getHighResImage(img) {
    // Try to get the MAXIMUM resolution image available (100% quality)
    let src = img.src;

    // Check srcset for highest resolution
    if (img.srcset) {
      const srcsetParts = img.srcset.split(',');
      let highestRes = { width: 0, src: src };

      srcsetParts.forEach(part => {
        const [url, descriptor] = part.trim().split(' ');
        if (descriptor) {
          const width = parseInt(descriptor.replace('w', ''));
          if (width > highestRes.width) {
            highestRes = { width, src: url };
          }
        }
      });

      if (highestRes.width > 0) {
        src = highestRes.src;
      }
    }

    // For Shopify CDN images, request MAXIMUM quality (master image)
    if (src.includes('cdn.shopify.com')) {
      // Remove ALL size constraints to get original master image
      // Shopify supports up to 5760x5760 - we request master for 100% quality
      src = src.replace(/_\d+x\d*\./, '.');
      src = src.replace(/_\d+x\d*_crop_[a-z]+\./, '.');
      src = src.replace(/&width=\d+/, '');
      src = src.replace(/\?width=\d+/, '?');
      src = src.replace(/&height=\d+/, '');
      src = src.replace(/\?height=\d+/, '?');
      src = src.replace(/&crop=[a-z]+/, '');
      src = src.replace(/\?$/, '');

      // If URL uses the new format with query params, request max size
      if (src.includes('?') || src.includes('&')) {
        if (!src.includes('width=')) {
          src += (src.includes('?') ? '&' : '?') + 'width=5760';
        }
      }
    }

    return src;
  }

  // Method to update zoom level
  setZoomLevel(level) {
    this.zoomLevel = level;
  }

  // Method to update lens size
  setLensSize(size) {
    this.lensSize = size;
    if (this.lens) {
      this.lens.style.width = `${size}px`;
      this.lens.style.height = `${size}px`;
    }
  }
}

// Initialize on product pages only
if (window.location.pathname.includes('/products/')) {
  window.productImageZoom = new ProductImageZoom();
}
